import { styled, useTheme } from '@mui/material';
import { Grid } from '@mui/material';
import { AppTextInput, FormLabel } from 'components/FormElements';
import { Control, Controller } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import HorizontalLabelInputGroup from 'components/HorizontalLabelInputGroup';
import { realisation as rules } from 'further-ui/validations';
import useConsiderationCalculations, {
  ConsiderationTotalType,
} from '../../hooks/useConsiderationCalculations';

type Props = {
  control: Control<rules.CreateRealisationFormValues>;
  index: number;
  currencySymbol: string;
  disabled: boolean;
};

const CenteredFormLabel = styled(FormLabel)(({ theme }) => ({
  whiteSpace: 'nowrap',
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  [theme.breakpoints.down('lg')]: {
    marginBottom: theme.spacing(2),
  },
}));

const WithholdingTaxConsiderationRow: React.FC<Props> = ({
  control,
  index,
  currencySymbol,
  disabled,
}) => {
  const theme = useTheme();
  const { onWithholdingConsiderationTaxChange, setFieldFocus } =
    useConsiderationCalculations();

  return (
    <Grid container spacing={3}>
      <Grid size={{ md: 5, xs: 12 }}>
        <CenteredFormLabel
          title="Withholding tax payable"
          labelColor={theme.palette.text.secondary}
          tooltipText="This is the total withholding tax payable by the fund and its limited partners as part of the realisation. All consideration lines above should exclude withholding tax. If withholding tax is the buyer’s responsibility or is paid on the fund’s behalf (and not recorded as a fund-level expense), leave this field empty."
        />
      </Grid>
      <Grid container size={{ md: 7, xs: 12 }}>
        <Grid size={{ md: 6, xs: false }}></Grid>
        <HorizontalLabelInputGroup
          label="Total"
          labelColor={theme.palette.text.secondary}
          size={6}
          fullWidth
        >
          <Controller
            name={`consideration.${index}.proRataConsideration.withholdingTaxPayable`}
            control={control}
            render={({
              field: { ref, onChange, ...rest },
              fieldState: { error },
            }) => (
              <NumericFormat
                {...rest}
                disabled={disabled}
                inputRef={ref}
                onValueChange={({ value }) => {
                  onChange(+value);
                  onWithholdingConsiderationTaxChange(+value);
                }}
                onFocus={() => {
                  setFieldFocus(ConsiderationTotalType.WithholdingTax);
                }}
                prefix={currencySymbol}
                decimalScale={10}
                allowNegative={false}
                thousandSeparator={true}
                customInput={AppTextInput}
                error={!!error}
                helperText={error?.message}
                size="small"
              />
            )}
          />
        </HorizontalLabelInputGroup>
      </Grid>
    </Grid>
  );
};

export default WithholdingTaxConsiderationRow;
